import { useState } from 'react'
import { GlassmorphicCard } from '@/components/ui/glassmorphic-card'
import { IndianRupee, TrendingUp, CreditCard, Download, Tag, RotateCcw, Plus, X, FileText, Search, Filter, BarChart3, ShoppingCart, Users, Star, ArrowUpRight, ArrowDownRight, Minus } from 'lucide-react'
import { mockPayments, mockCoupons, mockTopSellingCourses, mockMonthlyEnrollments, mockConversionFunnel, type Coupon, type PaymentRecord } from '@/data/admin-mock-data'
import { motion, AnimatePresence } from 'framer-motion'
import { toast } from 'sonner'

type RevenueTab = 'overview' | 'transactions' | 'reports' | 'refunds' | 'coupons' | 'sales'

export function AdminRevenueView() {
    const [activeTab, setActiveTab] = useState<RevenueTab>('overview')
    const [payments, setPayments] = useState<PaymentRecord[]>(mockPayments)
    const [coupons, setCoupons] = useState<Coupon[]>(mockCoupons)

    // Coupon State
    const [couponModalOpen, setCouponModalOpen] = useState(false)
    const [newCoupon, setNewCoupon] = useState<{ code: string, type: 'percentage' | 'flat', value: number }>({ code: '', type: 'percentage', value: 0 })

    // Refund Request State
    const [refundModalOpen, setRefundModalOpen] = useState(false)
    const [refundRequestFor, setRefundRequestFor] = useState<PaymentRecord | null>(null)
    const [refundReason, setRefundReason] = useState('')

    // Search & Filter State
    const [searchQuery, setSearchQuery] = useState('')
    const [statusFilter, setStatusFilter] = useState<string>('all')

    const total = payments.reduce((s, p) => s + (p.status === 'completed' ? p.amount : 0), 0)
    const pending = payments.filter(p => p.status === 'pending').reduce((s, p) => s + p.amount, 0)

    // Filtered Payments for Transactions Tab
    const filteredPayments = payments.filter(p => {
        const matchesSearch = p.studentName.toLowerCase().includes(searchQuery.toLowerCase()) || p.id.toLowerCase().includes(searchQuery.toLowerCase())
        const matchesStatus = statusFilter === 'all' || p.status === statusFilter
        return matchesSearch && matchesStatus
    })

    const handleRequestRefund = (p: PaymentRecord) => {
        setRefundRequestFor(p)
        setRefundReason('')
        setRefundModalOpen(true)
    }

    const submitRefundRequest = (e: React.FormEvent) => {
        e.preventDefault()
        if (!refundRequestFor) return

        setPayments(prev => prev.map(p => p.id === refundRequestFor.id ? { ...p, status: 'refund_requested', refundReason } : p))
        setRefundModalOpen(false)
        setRefundRequestFor(null)
        toast.success('Refund requested successfully')
    }

    const processRefund = (id: string) => {
        if (confirm('Are you sure you want to process this refund? The amount will be returned to the student.')) {
            setPayments(prev => prev.map(p => p.id === id ? { ...p, status: 'refunded' } : p))
            toast.success('Refund processed successfully')
        }
    }

    const handleCreateCoupon = (e: React.FormEvent) => {
        e.preventDefault()
        setCoupons(prev => [...prev, {
            id: `cp-${Date.now()}`,
            code: newCoupon.code.toUpperCase(),
            type: newCoupon.type,
            value: newCoupon.value,
            expiryDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // +30 days
            usageLimit: 100,
            usageCount: 0,
            isActive: true
        }])
        setCouponModalOpen(false)
        setNewCoupon({ code: '', type: 'percentage', value: 0 })
        toast.success('Coupon created successfully')
    }

    const toggleCouponStatus = (id: string) => {
        setCoupons(prev => prev.map(c => c.id === id ? { ...c, isActive: !c.isActive } : c))
    }

    // Report Calculations
    const revenueByType = {
        course: payments.filter(p => p.type === 'course' && p.status === 'completed').reduce((s, p) => s + p.amount, 0),
        test: payments.filter(p => p.type === 'test' && p.status === 'completed').reduce((s, p) => s + p.amount, 0),
        subscription: payments.filter(p => p.type === 'subscription' && p.status === 'completed').reduce((s, p) => s + p.amount, 0),
    }
    const maxRevenue = Math.max(revenueByType.course, revenueByType.test, revenueByType.subscription) || 1

    return (
        <div className="space-y-6 pb-24 lg:pb-0">
            <div>
                <h2 className="text-2xl font-bold text-theme-primary">Revenue & Payments</h2>
                <p className="text-sm text-theme-secondary">View revenue, manage refunds, and create discount coupons</p>
            </div>

            {/* Navigation Tabs */}
            <div className="flex items-center gap-2 overflow-x-auto pb-2 custom-scrollbar">
                {[
                    { id: 'overview', label: 'Overview', icon: TrendingUp },
                    { id: 'sales', label: 'Sales Dashboard', icon: BarChart3 },
                    { id: 'transactions', label: 'Transactions', icon: IndianRupee },
                    { id: 'reports', label: 'Reports', icon: FileText },
                    { id: 'refunds', label: 'Refunds', icon: RotateCcw },
                    { id: 'coupons', label: 'Coupons', icon: Tag },
                ].map((tab) => {
                    const Icon = tab.icon
                    return (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id as RevenueTab)}
                            className={`flex items-center gap-2 px-4 py-2 rounded-xl transition-all whitespace-nowrap ${activeTab === tab.id
                                ? 'bg-primary-blue text-white shadow-lg shadow-blue-500/25'
                                : 'bg-white dark:bg-slate-800 text-theme-secondary hover:bg-slate-50 dark:hover:bg-slate-700'
                                }`}
                        >
                            <Icon className="h-4 w-4" />
                            <span className="font-medium text-sm">{tab.label}</span>
                        </button>
                    )
                })}
            </div>

            <AnimatePresence mode="wait">
                {activeTab === 'overview' && (
                    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <GlassmorphicCard className="p-5">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm text-theme-secondary">Total Revenue</p>
                                        <p className="text-2xl font-bold text-theme-primary mt-1 flex items-center gap-1">
                                            <IndianRupee className="h-6 w-6" />
                                            {total.toLocaleString('en-IN')}
                                        </p>
                                    </div>
                                    <div className="w-12 h-12 rounded-xl bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                                        <TrendingUp className="h-6 w-6 text-green-600" />
                                    </div>
                                </div>
                            </GlassmorphicCard>
                            <GlassmorphicCard className="p-5">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm text-theme-secondary">Pending</p>
                                        <p className="text-2xl font-bold text-theme-primary mt-1 flex items-center gap-1">
                                            <IndianRupee className="h-6 w-6" />
                                            {pending.toLocaleString('en-IN')}
                                        </p>
                                    </div>
                                    <div className="w-12 h-12 rounded-xl bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                                        <CreditCard className="h-6 w-6 text-amber-600" />
                                    </div>
                                </div>
                            </GlassmorphicCard>
                            <GlassmorphicCard className="p-5">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm text-theme-secondary">Transactions</p>
                                        <p className="text-2xl font-bold text-theme-primary mt-1">{payments.length}</p>
                                    </div>
                                </div>
                            </GlassmorphicCard>
                        </div>

                        {/* Recent Transactions Table */}
                        <GlassmorphicCard className="overflow-hidden">
                            <div className="p-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
                                <h3 className="font-semibold text-theme-primary">Recent Payments</h3>
                                <button onClick={() => setActiveTab('transactions')} className="text-sm text-primary-blue hover:underline">View All</button>
                            </div>
                            <div className="overflow-x-auto">
                                <table className="w-full text-left">
                                    <thead>
                                        <tr className="border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
                                            <th className="p-4 text-sm font-semibold text-theme-primary">Date</th>
                                            <th className="p-4 text-sm font-semibold text-theme-primary">Student</th>
                                            <th className="p-4 text-sm font-semibold text-theme-primary">Type</th>
                                            <th className="p-4 text-sm font-semibold text-theme-primary">Amount</th>
                                            <th className="p-4 text-sm font-semibold text-theme-primary">Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {payments.slice(0, 5).map((p) => (
                                            <tr key={p.id} className="border-b border-slate-100 dark:border-slate-800 last:border-0">
                                                <td className="p-4 text-theme-secondary">{p.date}</td>
                                                <td className="p-4 font-medium text-theme-primary">{p.studentName}</td>
                                                <td className="p-4 text-theme-secondary capitalize">{p.type}</td>
                                                <td className="p-4 font-medium text-theme-primary">₹{p.amount.toLocaleString('en-IN')}</td>
                                                <td className="p-4">
                                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${p.status === 'completed' ? 'bg-green-100 dark:bg-green-900/30 text-green-700' : p.status === 'pending' ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700' : p.status === 'refund_requested' ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700' : 'bg-red-100 dark:bg-red-900/30 text-red-700'}`}>
                                                        {p.status.replace('_', ' ')}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </GlassmorphicCard>
                    </motion.div>
                )}

                {activeTab === 'transactions' && (
                    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="space-y-6">
                        <GlassmorphicCard className="p-4">
                            <div className="flex flex-col md:flex-row gap-4">
                                <div className="relative flex-1">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-theme-secondary" />
                                    <input
                                        type="text"
                                        placeholder="Search by student name or ID..."
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        className="w-full pl-10 pr-4 py-2 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-theme-primary focus:outline-none focus:ring-2 focus:ring-primary-blue/50"
                                    />
                                </div>
                                <div className="flex items-center gap-2">
                                    <Filter className="h-4 w-4 text-theme-secondary" />
                                    <select
                                        value={statusFilter}
                                        onChange={(e) => setStatusFilter(e.target.value)}
                                        className="px-4 py-2 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-theme-primary focus:outline-none focus:ring-2 focus:ring-primary-blue/50"
                                    >
                                        <option value="all">All Status</option>
                                        <option value="completed">Completed</option>
                                        <option value="pending">Pending</option>
                                        <option value="refunded">Refunded</option>
                                        <option value="refund_requested">Refund Requested</option>
                                    </select>
                                    <button className="inline-flex items-center gap-2 px-4 py-2 bg-slate-100 dark:bg-slate-800 text-theme-primary rounded-lg hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors">
                                        <Download className="h-4 w-4" />
                                        Export
                                    </button>
                                </div>
                            </div>
                        </GlassmorphicCard>

                        <GlassmorphicCard className="overflow-hidden">
                            <div className="overflow-x-auto">
                                <table className="w-full text-left">
                                    <thead>
                                        <tr className="border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
                                            <th className="p-4 text-sm font-semibold text-theme-primary">Date</th>
                                            <th className="p-4 text-sm font-semibold text-theme-primary">ID</th>
                                            <th className="p-4 text-sm font-semibold text-theme-primary">Student</th>
                                            <th className="p-4 text-sm font-semibold text-theme-primary">Type</th>
                                            <th className="p-4 text-sm font-semibold text-theme-primary">Amount</th>
                                            <th className="p-4 text-sm font-semibold text-theme-primary">Status</th>
                                            <th className="p-4 text-sm font-semibold text-theme-primary">Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredPayments.map((p) => (
                                            <tr key={p.id} className="border-b border-slate-100 dark:border-slate-800 last:border-0 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                                                <td className="p-4 text-theme-secondary">{p.date}</td>
                                                <td className="p-4 text-xs font-mono text-theme-secondary">{p.id}</td>
                                                <td className="p-4 font-medium text-theme-primary">{p.studentName}</td>
                                                <td className="p-4 text-theme-secondary capitalize">{p.type}</td>
                                                <td className="p-4 font-medium text-theme-primary">₹{p.amount.toLocaleString('en-IN')}</td>
                                                <td className="p-4">
                                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${p.status === 'completed' ? 'bg-green-100 dark:bg-green-900/30 text-green-700' : p.status === 'pending' ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700' : p.status === 'refund_requested' ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700' : 'bg-red-100 dark:bg-red-900/30 text-red-700'}`}>
                                                        {p.status.replace('_', ' ')}
                                                    </span>
                                                </td>
                                                <td className="p-4">
                                                    {p.status === 'completed' && (
                                                        <button
                                                            onClick={() => handleRequestRefund(p)}
                                                            className="text-xs px-3 py-1.5 border border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg text-theme-secondary transition-colors"
                                                        >
                                                            Request Refund
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            {filteredPayments.length === 0 && (
                                <div className="p-8 text-center text-theme-secondary">
                                    No transactions found matching your filters.
                                </div>
                            )}
                        </GlassmorphicCard>
                    </motion.div>
                )}

                {activeTab === 'reports' && (
                    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="space-y-6">
                        <GlassmorphicCard className="p-6">
                            <h3 className="text-lg font-bold text-theme-primary mb-6">Revenue Structure</h3>
                            <div className="space-y-6">
                                {Object.entries(revenueByType).map(([type, amount]) => (
                                    <div key={type} className="space-y-2">
                                        <div className="flex justify-between text-sm">
                                            <span className="font-medium text-theme-primary capitalize">{type} Sales</span>
                                            <span className="text-theme-secondary">₹{amount.toLocaleString('en-IN')}</span>
                                        </div>
                                        <div className="h-3 w-full bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                                            <div
                                                className="h-full bg-primary-blue rounded-full transition-all duration-1000"
                                                style={{ width: `${(amount / maxRevenue) * 100}%` }}
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </GlassmorphicCard>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <GlassmorphicCard className="p-5">
                                <h3 className="font-bold text-theme-primary mb-2">Monthly Growth</h3>
                                <p className="text-3xl font-bold text-green-600">+12.5%</p>
                                <p className="text-xs text-theme-secondary mt-1">Compared to last month</p>
                            </GlassmorphicCard>
                            <GlassmorphicCard className="p-5">
                                <h3 className="font-bold text-theme-primary mb-2">Avg. Order Value</h3>
                                <p className="text-3xl font-bold text-primary-blue">₹14,500</p>
                                <p className="text-xs text-theme-secondary mt-1">Based on recent transactions</p>
                            </GlassmorphicCard>
                        </div>
                    </motion.div>
                )}

                {activeTab === 'refunds' && (
                    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
                        <GlassmorphicCard className="overflow-hidden">
                            <div className="p-4 border-b border-slate-200 dark:border-slate-700">
                                <h3 className="text-lg font-bold text-theme-primary">Refund Requests</h3>
                                <p className="text-sm text-theme-secondary">Process pending refund requests</p>
                            </div>
                            <div className="overflow-x-auto">
                                <table className="w-full text-left">
                                    <thead>
                                        <tr className="border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
                                            <th className="p-4 text-sm font-semibold text-theme-primary">Date</th>
                                            <th className="p-4 text-sm font-semibold text-theme-primary">Student</th>
                                            <th className="p-4 text-sm font-semibold text-theme-primary">Amount</th>
                                            <th className="p-4 text-sm font-semibold text-theme-primary">Reason</th>
                                            <th className="p-4 text-sm font-semibold text-theme-primary">Status</th>
                                            <th className="p-4 text-sm font-semibold text-theme-primary">Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {payments.filter(p => p.status === 'refund_requested' || p.status === 'refunded').map((p) => (
                                            <tr key={p.id} className="border-b border-slate-100 dark:border-slate-800 last:border-0 bg-white dark:bg-slate-900/20">
                                                <td className="p-4 text-theme-secondary">{p.date}</td>
                                                <td className="p-4 font-medium text-theme-primary">{p.studentName}</td>
                                                <td className="p-4 font-medium text-theme-primary">₹{p.amount.toLocaleString('en-IN')}</td>
                                                <td className="p-4 text-sm text-theme-secondary italic max-w-xs">{p.refundReason || 'No reason provided'}</td>
                                                <td className="p-4">
                                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${p.status === 'refunded' ? 'bg-red-100 dark:bg-red-900/30 text-red-700' : 'bg-purple-100 dark:bg-purple-900/30 text-purple-700'}`}>
                                                        {p.status.replace('_', ' ')}
                                                    </span>
                                                </td>
                                                <td className="p-4">
                                                    {p.status === 'refund_requested' && (
                                                        <button
                                                            onClick={() => processRefund(p.id)}
                                                            className="text-xs px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors shadow-sm"
                                                        >
                                                            Process Refund
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                        {payments.filter(p => p.status === 'refund_requested' || p.status === 'refunded').length === 0 && (
                                            <tr>
                                                <td colSpan={6} className="p-8 text-center text-theme-secondary">
                                                    No refund requests found.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </GlassmorphicCard>
                    </motion.div>
                )}

                {activeTab === 'coupons' && (
                    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="space-y-6">
                        <div className="flex justify-between items-center">
                            <div>
                                <h3 className="text-xl font-bold text-theme-primary">Discount Coupons</h3>
                                <p className="text-sm text-theme-secondary">Manage promotional codes</p>
                            </div>
                            <button onClick={() => setCouponModalOpen(true)} className="flex items-center gap-2 px-4 py-2 bg-primary-blue hover:bg-blue-600 text-white rounded-xl transition-colors">
                                <Plus className="h-4 w-4" /> Create Coupon
                            </button>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {coupons.map(c => (
                                <GlassmorphicCard key={c.id} className={`p-5 relative overflow-hidden ${!c.isActive && 'opacity-70 grayscale'}`}>
                                    <div className="flex justify-between items-start mb-3">
                                        <div className="px-3 py-1 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 font-mono font-bold rounded text-lg tracking-wider border border-dashed border-blue-200 dark:border-blue-800">
                                            {c.code}
                                        </div>
                                        <button onClick={() => toggleCouponStatus(c.id)} className={`text-xs px-2 py-1 rounded-full transition-colors ${c.isActive ? 'bg-green-100 text-green-700' : 'bg-slate-200 text-slate-600'}`}>
                                            {c.isActive ? 'Active' : 'Deactivated'}
                                        </button>
                                    </div>
                                    <p className="font-bold text-2xl text-theme-primary mb-1">
                                        {c.type === 'percentage' ? `${c.value}% OFF` : `₹${c.value} OFF`}
                                    </p>
                                    <div className="text-xs text-theme-secondary space-y-1 mt-4">
                                        <p>Expires: {c.expiryDate}</p>
                                        <p>Used: {c.usageCount} / {c.usageLimit}</p>
                                    </div>
                                </GlassmorphicCard>
                            ))}
                        </div>
                    </motion.div>
                )}
                {activeTab === 'sales' && (
                    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="space-y-6">
                        {/* Sales Summary Cards */}
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                            <GlassmorphicCard className="p-5">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm text-theme-secondary">Total Courses Sold</p>
                                        <p className="text-2xl font-bold text-theme-primary mt-1">{mockTopSellingCourses.reduce((s, c) => s + c.studentsEnrolled, 0).toLocaleString('en-IN')}</p>
                                    </div>
                                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
                                        <ShoppingCart className="h-6 w-6 text-white" />
                                    </div>
                                </div>
                            </GlassmorphicCard>
                            <GlassmorphicCard className="p-5">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm text-theme-secondary">Students Enrolled</p>
                                        <p className="text-2xl font-bold text-theme-primary mt-1">{mockMonthlyEnrollments.reduce((s, m) => s + m.enrollments, 0).toLocaleString('en-IN')}</p>
                                    </div>
                                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
                                        <Users className="h-6 w-6 text-white" />
                                    </div>
                                </div>
                            </GlassmorphicCard>
                            <GlassmorphicCard className="p-5">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm text-theme-secondary">Total Sales Revenue</p>
                                        <p className="text-2xl font-bold text-theme-primary mt-1 flex items-center gap-1">
                                            <IndianRupee className="h-5 w-5" />
                                            {(mockTopSellingCourses.reduce((s, c) => s + c.revenue, 0) / 100000).toFixed(1)}L
                                        </p>
                                    </div>
                                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center">
                                        <TrendingUp className="h-6 w-6 text-white" />
                                    </div>
                                </div>
                            </GlassmorphicCard>
                            <GlassmorphicCard className="p-5">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm text-theme-secondary">Avg Conversion Rate</p>
                                        <p className="text-2xl font-bold text-theme-primary mt-1">
                                            {(mockTopSellingCourses.reduce((s, c) => s + c.conversionRate, 0) / mockTopSellingCourses.length).toFixed(1)}%
                                        </p>
                                    </div>
                                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center">
                                        <BarChart3 className="h-6 w-6 text-white" />
                                    </div>
                                </div>
                            </GlassmorphicCard>
                        </div>

                        {/* Top Selling Courses */}
                        <GlassmorphicCard className="overflow-hidden">
                            <div className="p-4 border-b border-slate-200 dark:border-slate-700">
                                <h3 className="text-lg font-bold text-theme-primary flex items-center gap-2">
                                    <Star className="h-5 w-5 text-amber-500" /> Top Selling Courses
                                </h3>
                                <p className="text-sm text-theme-secondary mt-0.5">Ranked by student enrollment count</p>
                            </div>
                            <div className="overflow-x-auto">
                                <table className="w-full text-left">
                                    <thead>
                                        <tr className="border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
                                            <th className="p-4 text-xs font-semibold text-theme-secondary uppercase">Rank</th>
                                            <th className="p-4 text-xs font-semibold text-theme-secondary uppercase">Course</th>
                                            <th className="p-4 text-xs font-semibold text-theme-secondary uppercase">Type</th>
                                            <th className="p-4 text-xs font-semibold text-theme-secondary uppercase">Enrolled</th>
                                            <th className="p-4 text-xs font-semibold text-theme-secondary uppercase">Revenue</th>
                                            <th className="p-4 text-xs font-semibold text-theme-secondary uppercase">Conv. Rate</th>
                                            <th className="p-4 text-xs font-semibold text-theme-secondary uppercase">Trend</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                        {mockTopSellingCourses.map((course, index) => (
                                            <tr key={course.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                                                <td className="p-4">
                                                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center font-bold text-sm ${
                                                        index === 0 ? 'bg-gradient-to-br from-amber-400 to-amber-500 text-white' :
                                                        index === 1 ? 'bg-gradient-to-br from-slate-300 to-slate-400 text-white' :
                                                        index === 2 ? 'bg-gradient-to-br from-orange-400 to-orange-500 text-white' :
                                                        'bg-slate-100 dark:bg-slate-800 text-theme-secondary'
                                                    }`}>
                                                        {index + 1}
                                                    </div>
                                                </td>
                                                <td className="p-4">
                                                    <div className="font-semibold text-theme-primary text-sm">{course.title}</div>
                                                    <div className="text-xs text-theme-secondary mt-0.5">{course.category}</div>
                                                </td>
                                                <td className="p-4">
                                                    <span className="px-2 py-0.5 rounded-full bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 text-xs font-medium">{course.type}</span>
                                                </td>
                                                <td className="p-4">
                                                    <div className="flex items-center gap-2">
                                                        <Users className="h-4 w-4 text-theme-secondary" />
                                                        <span className="font-bold text-theme-primary">{course.studentsEnrolled.toLocaleString('en-IN')}</span>
                                                    </div>
                                                </td>
                                                <td className="p-4 font-semibold text-theme-primary">₹{(course.revenue / 100000).toFixed(1)}L</td>
                                                <td className="p-4">
                                                    <div className="flex items-center gap-2">
                                                        <div className="w-16 h-2 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                                                            <div className="h-full bg-primary-blue rounded-full" style={{ width: `${course.conversionRate}%` }} />
                                                        </div>
                                                        <span className="text-sm font-medium text-theme-primary">{course.conversionRate}%</span>
                                                    </div>
                                                </td>
                                                <td className="p-4">
                                                    <div className={`flex items-center gap-1 text-sm font-semibold ${
                                                        course.trend === 'up' ? 'text-emerald-600' : course.trend === 'down' ? 'text-red-500' : 'text-slate-500'
                                                    }`}>
                                                        {course.trend === 'up' ? <ArrowUpRight className="h-4 w-4" /> : course.trend === 'down' ? <ArrowDownRight className="h-4 w-4" /> : <Minus className="h-4 w-4" />}
                                                        {Math.abs(course.trendPercent)}%
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </GlassmorphicCard>

                        {/* Monthly Enrollments Chart & Conversion Funnel */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            {/* Monthly Enrollments Bar Chart */}
                            <GlassmorphicCard className="p-6">
                                <h3 className="text-lg font-bold text-theme-primary mb-1">Monthly Enrollments</h3>
                                <p className="text-sm text-theme-secondary mb-6">Student enrollment trend over 7 months</p>
                                <div className="flex items-end gap-3 h-48">
                                    {mockMonthlyEnrollments.map((month) => {
                                        const maxEnroll = Math.max(...mockMonthlyEnrollments.map(m => m.enrollments))
                                        const height = (month.enrollments / maxEnroll) * 100
                                        return (
                                            <div key={month.shortMonth} className="flex-1 flex flex-col items-center gap-2">
                                                <span className="text-xs font-bold text-theme-primary">{month.enrollments}</span>
                                                <div className="w-full relative" style={{ height: `${height}%` }}>
                                                    <div className="absolute inset-0 bg-gradient-to-t from-primary-blue to-blue-400 rounded-t-lg opacity-90 hover:opacity-100 transition-opacity cursor-pointer" />
                                                </div>
                                                <span className="text-xs text-theme-secondary font-medium">{month.shortMonth}</span>
                                            </div>
                                        )
                                    })}
                                </div>
                                <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700 flex items-center justify-between">
                                    <p className="text-sm text-theme-secondary">Total enrolled</p>
                                    <p className="text-lg font-bold text-primary-blue">{mockMonthlyEnrollments.reduce((s, m) => s + m.enrollments, 0).toLocaleString('en-IN')}</p>
                                </div>
                            </GlassmorphicCard>

                            {/* Conversion Funnel */}
                            <GlassmorphicCard className="p-6">
                                <h3 className="text-lg font-bold text-theme-primary mb-1">Top Conversions</h3>
                                <p className="text-sm text-theme-secondary mb-6">Visitor to completion funnel</p>
                                <div className="space-y-3">
                                    {mockConversionFunnel.map((stage, index) => {
                                        const maxCount = mockConversionFunnel[0].count
                                        const width = (stage.count / maxCount) * 100
                                        const convRate = index > 0
                                            ? ((stage.count / mockConversionFunnel[index - 1].count) * 100).toFixed(1)
                                            : '100'
                                        return (
                                            <div key={stage.stage} className="space-y-1.5">
                                                <div className="flex justify-between items-center">
                                                    <span className="text-sm font-medium text-theme-primary">{stage.stage}</span>
                                                    <div className="flex items-center gap-3">
                                                        <span className="text-sm font-bold text-theme-primary">{stage.count.toLocaleString('en-IN')}</span>
                                                        {index > 0 && (
                                                            <span className="text-xs px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-theme-secondary">{convRate}%</span>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="h-3 w-full bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                                                    <motion.div
                                                        initial={{ width: 0 }}
                                                        animate={{ width: `${width}%` }}
                                                        transition={{ duration: 1, delay: index * 0.15 }}
                                                        className={`h-full bg-gradient-to-r ${stage.color} rounded-full`}
                                                    />
                                                </div>
                                            </div>
                                        )
                                    })}
                                </div>
                                <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
                                    <div className="flex items-center justify-between">
                                        <p className="text-sm text-theme-secondary">Overall conversion</p>
                                        <p className="text-lg font-bold text-emerald-600">
                                            {((mockConversionFunnel[mockConversionFunnel.length - 1].count / mockConversionFunnel[0].count) * 100).toFixed(1)}%
                                        </p>
                                    </div>
                                </div>
                            </GlassmorphicCard>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Create Coupon Modal */}
            {couponModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setCouponModalOpen(false)}>
                    <GlassmorphicCard className="w-full max-w-md p-6 shadow-xl" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-bold text-theme-primary">Create New Coupon</h3>
                            <button onClick={() => setCouponModalOpen(false)} className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800"><X className="h-5 w-5" /></button>
                        </div>
                        <form onSubmit={handleCreateCoupon} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-theme-secondary mb-1">Coupon Code</label>
                                <input value={newCoupon.code} onChange={e => setNewCoupon(c => ({ ...c, code: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '') }))} className="w-full px-4 py-2 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-theme-primary" placeholder="e.g. SUMMER25" required maxLength={10} />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-theme-secondary mb-1">Discount Type</label>
                                    <select value={newCoupon.type} onChange={e => setNewCoupon(c => ({ ...c, type: e.target.value as any }))} className="w-full px-4 py-2 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-theme-primary">
                                        <option value="percentage">Percentage (%)</option>
                                        <option value="flat">Flat Amount (₹)</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-theme-secondary mb-1">Value</label>
                                    <input type="number" value={newCoupon.value} onChange={e => setNewCoupon(c => ({ ...c, value: Number(e.target.value) }))} className="w-full px-4 py-2 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-theme-primary" required min="1" max={newCoupon.type === 'percentage' ? 100 : 99999} />
                                </div>
                            </div>
                            <button type="submit" className="w-full py-2 bg-primary-blue text-white font-semibold rounded-lg hover:bg-blue-600 transition-colors mt-2">
                                Create Coupon
                            </button>
                        </form>
                    </GlassmorphicCard>
                </div>
            )}

            {/* Request Refund Modal */}
            {refundModalOpen && refundRequestFor && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setRefundModalOpen(false)}>
                    <GlassmorphicCard className="w-full max-w-md p-6 shadow-xl" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-bold text-theme-primary">Request Refund</h3>
                            <button onClick={() => setRefundModalOpen(false)} className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800"><X className="h-5 w-5" /></button>
                        </div>
                        <div className="mb-4 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                            <div className="flex justify-between text-sm mb-1">
                                <span className="text-theme-secondary">Student</span>
                                <span className="font-semibold text-theme-primary">{refundRequestFor.studentName}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                                <span className="text-theme-secondary">Amount</span>
                                <span className="font-semibold text-theme-primary">₹{refundRequestFor.amount.toLocaleString('en-IN')}</span>
                            </div>
                        </div>
                        <form onSubmit={submitRefundRequest} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-theme-secondary mb-1">Reason for Refund</label>
                                <textarea
                                    value={refundReason}
                                    onChange={(e) => setRefundReason(e.target.value)}
                                    className="w-full px-4 py-2 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-theme-primary h-24 resize-none"
                                    placeholder="Enter reason (e.g., Duplicate payment, Student withdrawal...)"
                                    required
                                />
                            </div>
                            <button type="submit" className="w-full py-2 bg-primary-blue text-white font-semibold rounded-lg hover:bg-blue-600 transition-colors">
                                Submit Request
                            </button>
                        </form>
                    </GlassmorphicCard>
                </div>
            )}
        </div>
    )
}
